// Block 3.7 «Датасет ДТ» — финальная таблица, готовая к выгрузке в Альта-Софт.
//
// Источник данных: GET /api/v1/deals/:id/dt-dataset (поток 2, реализуется
// отдельной веткой `feat/c4-build-dt-dataset`). Если endpoint 404/501 —
// показываем заглушку «API не готов» вместо crash.
//
// UI flow:
//   1. Брокер видит таблицу 31+ колонок (если датасет уже построен)
//   2. Inline-редактирует поля manufacturer/country/brand/article/composition/tech_specs
//   3. Жмёт «Сохранить правки» → POST /api/v1/deals/:id/dt-dataset/save
//   4. Жмёт «Экспорт в Альта XLSX» → GET /api/v1/deals/:id/dt-dataset/export?format=alta
//      Browser скачивает файл (Content-Disposition attachment).
//   5. Жмёт «Сформировать датасет» → POST /api/v1/deals/:id/build-dt-dataset
//
// © GSCG 2026 · UI block 3.7
import { useCallback, useEffect, useMemo, useState } from 'react';

type RowStatus = 'ok' | 'partial' | 'gap' | 'blocked';

export interface DtDatasetRow {
  artikul?: string | number | null;
  name_ru?: string | null;
  name_en?: string | null;
  name_cn?: string | null;
  brand?: string | null;
  manufacturer?: string | null;
  country_origin?: string | null;
  tnved_code?: string | null;
  tnved_name_ru?: string | null;
  description_for_dt?: string | null;
  material?: string | null;
  purpose?: string | null;
  composition?: string | null;
  tech_specs?: string | null;
  qty_basic?: number | string | null;
  unit_basic?: string | null;
  qty_additional?: number | string | null;
  unit_additional?: string | null;
  weight_gross?: number | string | null;
  weight_net?: number | string | null;
  price_per_unit?: number | string | null;
  invoice_currency?: string | null;
  invoice_value?: number | string | null;
  customs_value_rub?: number | string | null;
  duty_rate?: string | null;
  duty_rub?: number | string | null;
  vat_rate?: string | number | null;
  vat_rub?: number | string | null;
  customs_fee_rub?: number | string | null;
  required_documents?: Array<{
    code?: string | null;
    type?: string | null;
    number?: string | null;
    kind?: 'valid' | 'required_missing' | 'not_required' | 'unknown';
  }> | null;
  validation?: {
    completeness?: number;
    gaps?: string[];
    blockers?: string[];
    status?: RowStatus;
  } | null;
}

interface DtDatasetResponse {
  rows: DtDatasetRow[];
  built_at?: string | null;
  completeness?: number | null;
}

export interface DealDtDatasetSectionProps {
  dealId: string;
  token: string;
}

const EDITABLE_FIELDS: ReadonlyArray<keyof DtDatasetRow> = [
  'manufacturer',
  'country_origin',
  'brand',
  'artikul',
  'composition',
  'tech_specs',
];

const COLS: Array<{
  key: keyof DtDatasetRow;
  label: string;
  width: string;
  editable?: boolean;
}> = [
  { key: 'artikul', label: 'Артикул', width: 'w-24', editable: true },
  { key: 'description_for_dt', label: 'Описание гр.31', width: 'w-72' },
  { key: 'name_ru', label: 'Наим. RU', width: 'w-48' },
  { key: 'name_cn', label: 'Наим. CN', width: 'w-32' },
  { key: 'brand', label: 'Бренд', width: 'w-28', editable: true },
  { key: 'manufacturer', label: 'Производитель', width: 'w-40', editable: true },
  { key: 'country_origin', label: 'Страна происх.', width: 'w-24', editable: true },
  { key: 'tnved_code', label: 'ТН ВЭД', width: 'w-24' },
  { key: 'tnved_name_ru', label: 'Наим. по ТН ВЭД', width: 'w-48' },
  { key: 'qty_basic', label: 'Кол-во', width: 'w-20' },
  { key: 'unit_basic', label: 'Ед.', width: 'w-16' },
  { key: 'weight_gross', label: 'Брутто, кг', width: 'w-24' },
  { key: 'weight_net', label: 'Нетто, кг', width: 'w-24' },
  { key: 'price_per_unit', label: 'Цена/ед.', width: 'w-20' },
  { key: 'invoice_currency', label: 'Валюта', width: 'w-16' },
  { key: 'invoice_value', label: 'Инвойс', width: 'w-24' },
  { key: 'customs_value_rub', label: 'Тамож. ст-ть RUB', width: 'w-32' },
  { key: 'duty_rate', label: 'Ставка', width: 'w-20' },
  { key: 'duty_rub', label: 'Пошлина RUB', width: 'w-28' },
  { key: 'vat_rate', label: 'НДС %', width: 'w-16' },
  { key: 'vat_rub', label: 'НДС RUB', width: 'w-24' },
  { key: 'customs_fee_rub', label: 'Сбор RUB', width: 'w-24' },
  { key: 'material', label: 'Материал', width: 'w-28' },
  { key: 'composition', label: 'Состав', width: 'w-32', editable: true },
  { key: 'tech_specs', label: 'Тех. характ.', width: 'w-36', editable: true },
];

type ApiState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'not_ready'; reason: string }
  | { kind: 'ready'; data: DtDatasetResponse }
  | { kind: 'error'; message: string };

function isGap(row: DtDatasetRow, key: keyof DtDatasetRow): boolean {
  const v = row[key];
  if (v === null || v === undefined) return true;
  if (typeof v === 'string' && v.trim() === '') return true;
  return false;
}

function statusBadge(status: RowStatus | undefined): { label: string; className: string } {
  switch (status) {
    case 'ok':
      return { label: 'ok', className: 'bg-green-100 text-green-800 border-green-300' };
    case 'partial':
      return { label: 'partial', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' };
    case 'gap':
      return { label: 'gap', className: 'bg-orange-100 text-orange-800 border-orange-300' };
    case 'blocked':
      return { label: 'blocked', className: 'bg-red-100 text-red-800 border-red-300' };
    default:
      return { label: '—', className: 'bg-gray-100 text-gray-600 border-gray-300' };
  }
}

export function DealDtDatasetSection(props: DealDtDatasetSectionProps): JSX.Element {
  const [state, setState] = useState<ApiState>({ kind: 'idle' });
  const [edits, setEdits] = useState<Record<number, Partial<DtDatasetRow>>>({});
  const [savingEdits, setSavingEdits] = useState(false);
  const [building, setBuilding] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const r = await fetch(`/api/v1/deals/${props.dealId}/dt-dataset`, {
        headers: { Authorization: `Bearer ${props.token}` },
      });
      if (r.status === 204) {
        setState({ kind: 'not_ready', reason: 'Датасет ещё не построен. Нажмите «Сформировать датасет».' });
        return;
      }
      if (r.status === 404 || r.status === 501) {
        setState({ kind: 'not_ready', reason: 'API датасета ДТ ещё не задеплоен (поток 2).' });
        return;
      }
      if (!r.ok) {
        setState({ kind: 'error', message: `HTTP ${r.status}` });
        return;
      }
      const j = (await r.json()) as DtDatasetResponse | DtDatasetRow[];
      const data: DtDatasetResponse = Array.isArray(j) ? { rows: j } : j;
      setState({ kind: 'ready', data });
      setEdits({});
    } catch (e) {
      setState({ kind: 'error', message: e instanceof Error ? e.message : 'network error' });
    }
  }, [props.dealId, props.token]);

  useEffect(() => {
    void load();
  }, [load]);

  const triggerBuild = useCallback(async () => {
    setBuilding(true);
    try {
      const r = await fetch(`/api/v1/deals/${props.dealId}/build-dt-dataset`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${props.token}` },
      });
      if (!r.ok) {
        setToast(`Сборка датасета не удалась (HTTP ${r.status}). Поток 2 может быть ещё не задеплоен.`);
      } else {
        setToast('Датасет собран.');
        await load();
      }
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Сетевая ошибка');
    } finally {
      setBuilding(false);
      setTimeout(() => setToast(null), 4000);
    }
  }, [props.dealId, props.token, load]);

  const saveEdits = useCallback(async () => {
    if (state.kind !== 'ready' || Object.keys(edits).length === 0) return;
    setSavingEdits(true);
    try {
      // Merge edits into rows.
      const merged = state.data.rows.map((row, i) => ({ ...row, ...(edits[i] ?? {}) }));
      const r = await fetch(`/api/v1/deals/${props.dealId}/dt-dataset/save`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${props.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ rows: merged }),
      });
      if (!r.ok) {
        setToast(`Сохранение правок не удалось (HTTP ${r.status}).`);
      } else {
        setToast('Правки сохранены.');
        setEdits({});
        await load();
      }
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Сетевая ошибка');
    } finally {
      setSavingEdits(false);
      setTimeout(() => setToast(null), 4000);
    }
  }, [edits, state, props.dealId, props.token, load]);

  const summary = useMemo(() => {
    if (state.kind !== 'ready') return null;
    const rows = state.data.rows;
    const total = rows.length;
    const counts = { ok: 0, partial: 0, gap: 0, blocked: 0 } as Record<RowStatus, number>;
    let completenessSum = 0;
    let completenessN = 0;
    for (const r of rows) {
      const s = r.validation?.status;
      if (s && s in counts) counts[s]++;
      if (typeof r.validation?.completeness === 'number') {
        completenessSum += r.validation.completeness;
        completenessN++;
      }
    }
    const avgPct =
      completenessN > 0
        ? Math.round((completenessSum / completenessN / 31) * 100)
        : null;
    return { total, counts, avgPct };
  }, [state]);

  const downloadAlta = useCallback(async () => {
    if (state.kind !== 'ready') return;
    const gaps = summary?.counts.gap ?? 0;
    const blockers = summary?.counts.blocked ?? 0;
    if (gaps + blockers > 0) {
      const ok = window.confirm(
        `В датасете ${gaps} GAP-строк и ${blockers} blocked-строк. Всё равно экспортировать?`,
      );
      if (!ok) return;
    }
    setExporting(true);
    try {
      const r = await fetch(
        `/api/v1/deals/${props.dealId}/dt-dataset/export?format=alta`,
        { headers: { Authorization: `Bearer ${props.token}` } },
      );
      if (!r.ok) {
        const txt = await r.text();
        setToast(`Экспорт не удался: HTTP ${r.status}. ${txt.slice(0, 200)}`);
        return;
      }
      const blob = await r.blob();
      const cd = r.headers.get('content-disposition') ?? '';
      const m = cd.match(/filename="([^"]+)"/);
      const fname = m ? m[1]! : `DT_${props.dealId.slice(0, 8)}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setToast(`XLSX скачан: ${fname}. Откройте в Альта-Софт.`);
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Сетевая ошибка');
    } finally {
      setExporting(false);
      setTimeout(() => setToast(null), 5000);
    }
  }, [props.dealId, props.token, state, summary]);

  const setCellEdit = (rowIdx: number, key: keyof DtDatasetRow, value: string): void => {
    setEdits((prev) => ({
      ...prev,
      [rowIdx]: { ...(prev[rowIdx] ?? {}), [key]: value },
    }));
  };

  const dirtyCount = Object.keys(edits).length;

  return (
    <section className="mt-8 rounded-lg border border-gray-200 bg-white p-4">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            3.7. Датасет ДТ — готов к подаче в Альту
          </h2>
          {summary && (
            <p className="mt-1 text-sm text-gray-600">
              {summary.total} строк ·{' '}
              {summary.avgPct !== null ? `готовность ${summary.avgPct}%` : 'completeness н/д'} ·{' '}
              <span className="text-green-700">{summary.counts.ok} ok</span> ·{' '}
              <span className="text-yellow-700">{summary.counts.partial} partial</span> ·{' '}
              <span className="text-orange-700">{summary.counts.gap} gap</span> ·{' '}
              <span className="text-red-700">{summary.counts.blocked} blocked</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={triggerBuild}
            disabled={building}
            className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
          >
            {building ? 'Сборка…' : 'Сформировать датасет'}
          </button>
          <button
            type="button"
            onClick={saveEdits}
            disabled={savingEdits || dirtyCount === 0 || state.kind !== 'ready'}
            className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
          >
            {savingEdits ? 'Сохранение…' : `Сохранить правки${dirtyCount > 0 ? ` (${dirtyCount})` : ''}`}
          </button>
          <button
            type="button"
            onClick={downloadAlta}
            disabled={exporting || state.kind !== 'ready'}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {exporting ? 'Экспорт…' : 'Экспорт в Альта XLSX'}
          </button>
        </div>
      </header>

      {summary?.avgPct !== undefined && summary?.avgPct !== null && (
        <div className="mb-3">
          <div className="h-2 w-full overflow-hidden rounded bg-gray-200">
            <div
              className="h-full bg-blue-500"
              style={{ width: `${Math.min(100, Math.max(0, summary.avgPct))}%` }}
            />
          </div>
        </div>
      )}

      {state.kind === 'loading' && (
        <div className="py-8 text-center text-sm text-gray-500">Загрузка…</div>
      )}

      {state.kind === 'not_ready' && (
        <div className="rounded border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-900">
          {state.reason}
        </div>
      )}

      {state.kind === 'error' && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          Ошибка: {state.message}
        </div>
      )}

      {state.kind === 'ready' && state.data.rows.length === 0 && (
        <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
          В датасете нет строк. Approve хотя бы одного кандидата в блоке 3.3.
        </div>
      )}

      {state.kind === 'ready' && state.data.rows.length > 0 && (
        <div className="max-h-[480px] overflow-auto rounded border border-gray-200">
          <table className="min-w-full table-fixed border-collapse text-xs">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr>
                <th className="sticky left-0 z-20 w-20 border-b border-r bg-gray-50 px-2 py-2 text-left font-semibold text-gray-700">
                  Статус
                </th>
                {COLS.map((c) => (
                  <th
                    key={String(c.key)}
                    className={`${c.width} border-b border-r bg-gray-50 px-2 py-2 text-left font-semibold text-gray-700`}
                  >
                    {c.label}
                    {c.editable && <span className="ml-1 text-blue-500" title="редактируемое">✎</span>}
                  </th>
                ))}
                <th className="w-48 border-b bg-gray-50 px-2 py-2 text-left font-semibold text-gray-700">
                  Документы
                </th>
              </tr>
            </thead>
            <tbody>
              {state.data.rows.map((row, i) => {
                const merged = { ...row, ...(edits[i] ?? {}) };
                const badge = statusBadge(row.validation?.status);
                return (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                    <td className="sticky left-0 z-10 border-b border-r bg-inherit px-2 py-1">
                      <span className={`inline-block rounded border px-2 py-0.5 text-[10px] font-medium ${badge.className}`}>
                        {badge.label}
                      </span>
                    </td>
                    {COLS.map((c) => {
                      const v = merged[c.key];
                      const gap = isGap(merged, c.key) && !c.editable;
                      const editable = c.editable && EDITABLE_FIELDS.includes(c.key);
                      const displayVal = v === null || v === undefined ? '' : String(v);
                      return (
                        <td
                          key={String(c.key)}
                          className={`border-b border-r px-2 py-1 align-top ${
                            gap ? 'bg-yellow-50' : ''
                          }`}
                        >
                          {editable ? (
                            <input
                              type="text"
                              value={displayVal}
                              onChange={(e) => setCellEdit(i, c.key, e.target.value)}
                              className="w-full rounded border border-transparent bg-transparent px-1 py-0.5 text-xs hover:border-gray-300 focus:border-blue-500 focus:outline-none"
                            />
                          ) : (
                            <span className={displayVal === '' ? 'text-gray-400' : 'text-gray-900'}>
                              {displayVal || '—'}
                            </span>
                          )}
                        </td>
                      );
                    })}
                    <td className="border-b px-2 py-1 align-top">
                      {row.required_documents && row.required_documents.length > 0 ? (
                        <ul className="space-y-0.5">
                          {row.required_documents.map((d, j) => (
                            <li
                              key={j}
                              className={`truncate text-[11px] ${
                                d.kind === 'required_missing' ? 'text-red-700' : 'text-gray-700'
                              }`}
                              title={`${d.type ?? ''} ${d.number ?? ''}`}
                            >
                              {d.type ?? '?'}: {d.number ?? '—'}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {toast && (
        <div
          role="status"
          className="mt-3 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900"
        >
          {toast}
        </div>
      )}
    </section>
  );
}
